import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  BarChart3,
  CalendarDays,
  FileText,
  FolderOpen,
  Hourglass,
  Plus,
  Send,
  ShieldCheck,
  TrendingDown,
  TrendingUp,
  Trophy
} from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend
} from 'recharts';
import doiService from '../../../api/crm/doi/doiService';
import PermissionGate from '../../../components/common/PermissionGate';
import { formatDate } from '../../../utils/stringUtils';
import { formatIndianAmount, formatIndianNumber } from '../../../utils/amountFormatter';

const DASHBOARD_BLUE = '#0D6EFD';
const DASHBOARD_GREEN = '#16A34A';
const DASHBOARD_ORANGE = '#F97316';

const formatTrend = (trend) => trend || { label: 'No Prior Data', direction: 'neutral' };

const mapPlatformComplianceRow = (row = {}) => ({
  platform: row.platform || row._id || 'Unassigned',
  projectsWon: Number(row.projectsWon || 0),
  doiCreated: Number(row.doiCreated || 0),
  doiPending: Number(row.doiPending || 0),
  compliance: Number(row.compliance || 0)
});

const DoiDashboard = () => {
  const navigate = useNavigate();

  const {
    data: dashboardData,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['doiDashboardExecutive'],
    queryFn: async () => {
      const result = await doiService.getDashboardOverview({ recentPage: 1, recentLimit: 10 });
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch DOI dashboard overview');
      }
      return result.dashboard;
    },
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    retry: 1
  });

  const dashboard = React.useMemo(() => {
    if (!dashboardData) return null;

    const kpis = dashboardData.kpis || {};
    const trends = dashboardData.trends || {};
    const distribution = dashboardData.distribution || {};

    const trendData = dashboardData.trendData || [];
    const hasTrendData = trendData.some((item) => item.projectsWon || item.doiCreated || item.doiPending);
    const normalizedTrendData = hasTrendData
      ? trendData
      : Array.from({ length: 6 }).map((_, index) => {
          const date = new Date();
          date.setMonth(date.getMonth() - (5 - index), 1);
          return {
            month: date.toLocaleString('en-US', { month: 'short', year: 'numeric' }),
            projectsWon: 0,
            doiCreated: index === 5 ? (kpis.currentMonthDoiCreated || kpis.doiCreated || 0) : 0,
            doiPending: 0
          };
        });

    const platformRows = (dashboardData.platformSummary?.length
      ? dashboardData.platformSummary
      : dashboardData.compliance?.byPlatform) || [];
    let complianceData = platformRows.map(mapPlatformComplianceRow);
    if (complianceData.length === 0 && (kpis.totalProjectsWon || 0) > 0) {
      complianceData = [{
        platform: 'Overall',
        projectsWon: Number(kpis.totalProjectsWon || 0),
        doiCreated: Number(kpis.doiCreated || 0),
        doiPending: Number(kpis.doiPending || 0),
        compliance: Number(kpis.doiCompliance || 0)
      }];
    }

    return {
      cards: [
        { title: 'Total Projects Won', value: formatIndianNumber(kpis.totalProjectsWon || 0), description: 'Total projects successfully marked as Won across all platforms.', icon: Trophy, accent: 'blue', trend: formatTrend(trends.totalProjectsWon) },
        { title: 'DOI Created', value: formatIndianNumber(kpis.doiCreated || 0), description: 'Total DOI records created across all won opportunities.', icon: FileText, accent: 'green', trend: formatTrend(trends.doiCreated) },
        { title: 'DOI Pending', value: formatIndianNumber(kpis.doiPending || 0), description: 'Won opportunities still awaiting DOI (Total Projects Won minus DOI Created).', icon: Hourglass, accent: 'orange', trend: formatTrend(trends.doiPending) },
        { title: 'DOI Compliance', value: `${kpis.doiCompliance || 0}%`, description: 'Share of won opportunities with DOI documentation (DOI Created ÷ Total Projects Won).', icon: ShieldCheck, accent: 'purple', trend: formatTrend(trends.doiCompliance) },
        { title: 'DOI Shared Today', value: formatIndianNumber(kpis.doiSharedToday || 0), description: 'DOI announcements circulated internally today.', icon: Send, accent: 'blue', trend: formatTrend(trends.doiSharedToday) },
        { title: 'DOI Shared This Month', value: formatIndianNumber(kpis.doiSharedThisMonth || 0), description: 'DOI announcements circulated during the current month.', icon: CalendarDays, accent: 'green', trend: formatTrend(trends.doiSharedThisMonth) },
        { title: 'Current Month Won Projects', value: formatIndianNumber(kpis.currentMonthWonProjects || 0), description: 'Projects marked as Won during the current month.', icon: BarChart3, accent: 'blue', trend: formatTrend(trends.currentMonthWonProjects) },
        { title: 'Current Month Project Value', value: `₹${formatIndianAmount(kpis.currentMonthProjectValue || 0)}`, description: 'Combined value of projects won during the current month.', icon: FolderOpen, accent: 'green', trend: formatTrend(trends.currentMonthProjectValue) }
      ],
      trendData: normalizedTrendData,
      complianceData,
      distributionData: [
        { name: 'DOI Created', value: distribution.createdCount || 0, color: DASHBOARD_GREEN },
        { name: 'DOI Pending', value: distribution.pendingCount || 0, color: DASHBOARD_ORANGE }
      ],
      platformSummary: dashboardData.platformSummary || [],
      recentActivity: dashboardData.recentActivity || [],
      insights: dashboardData.insights || [],
      alerts: dashboardData.alerts || []
    };
  }, [dashboardData]);

  return (
    <div className="space-y-6 pb-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="flex items-center gap-4">
          <div className="bg-slate-900 p-2.5 rounded-xl shadow-sm border border-slate-800 flex items-center justify-center">
            <FileText className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">DOI Dashboard</h1>
            <p className="text-sm sm:text-base text-slate-500 mt-1">Executive overview of project-win announcements and DOI communication</p>
          </div>
        </div>
        <PermissionGate permission="quotation_management" action="create">
          <button
            onClick={() => navigate('/crm/quotation-proposal/create-quotes?createDoi=1')}
            className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8"
          >
            <Plus className="h-4 w-4" />
            <span>Create DOI</span>
          </button>
        </PermissionGate>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {isLoading ? (
          Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="bg-white border border-gray-200 rounded-lg p-5 animate-pulse">
              <div className="h-10 w-10 rounded-full bg-gray-200 mb-4" />
              <div className="h-4 bg-gray-200 rounded w-2/3 mb-3" />
              <div className="h-7 bg-gray-200 rounded w-1/3 mb-3" />
              <div className="h-3 bg-gray-200 rounded w-full" />
            </div>
          ))
        ) : isError ? (
          <div className="sm:col-span-2 xl:col-span-4 rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-700">
            {error?.message || 'Unable to load DOI dashboard data.'}
          </div>
        ) : dashboard?.cards.map((card) => {
          const Icon = card.icon;
          const accentClass = {
            blue: 'bg-blue-50 text-[#0F2942] border border-blue-100',
            green: 'bg-emerald-50 text-emerald-600 border border-emerald-100',
            orange: 'bg-amber-50 text-amber-600 border border-amber-100',
            purple: 'bg-purple-50 text-purple-600 border border-purple-100'
          }[card.accent] || 'bg-blue-50 text-[#0F2942] border border-blue-100';

          const cardBorder = {
            blue: 'border-blue-200/80 hover:border-blue-400',
            green: 'border-emerald-200/80 hover:border-emerald-400',
            orange: 'border-amber-200/80 hover:border-amber-400',
            purple: 'border-purple-200/80 hover:border-purple-400'
          }[card.accent] || 'border-blue-200/80 hover:border-blue-400';

          const trendColor = card.trend.direction === 'down' ? 'text-rose-600' : card.trend.direction === 'up' ? 'text-emerald-600' : 'text-slate-400';
          const TrendIcon = card.trend.direction === 'down' ? TrendingDown : TrendingUp;

          return (
            <div key={card.title} className={`bg-white border ${cardBorder} rounded-2xl p-5 shadow-2xs hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 cursor-pointer group`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-gray-700 truncate group-hover:text-blue-600 transition-colors">{card.title}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2 truncate">{card.value}</p>
                  <p className="text-xs text-gray-500 mt-2 leading-5">{card.description}</p>
                </div>
                <div className={`h-11 w-11 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform ${accentClass}`}>
                  <Icon className="h-5 w-5" />
                </div>
              </div>
              <div className={`mt-4 flex items-center gap-1 text-xs font-medium ${trendColor}`}>
                <TrendIcon className="h-3.5 w-3.5" />
                <span>{card.trend.label}</span>
              </div>
            </div>
          );
        })}
      </div>

      {dashboard && !isError && (
        <div className="grid grid-cols-1 gap-4">
          <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 bg-white border border-slate-200/90 rounded-2xl p-5 shadow-2xs">
                <h2 className="text-base font-semibold text-gray-900">DOI Trend (Last 6 Months)</h2>
                <p className="text-xs text-gray-500 mt-1">Projects won, DOI created, and pending documentation trend.</p>
                <div className="h-72 mt-4">
                  {dashboard.trendData.some((item) => item.projectsWon || item.doiCreated || item.doiPending) ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={dashboard.trendData} margin={{ top: 10, right: 18, left: -10, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} interval={0} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="projectsWon" name="Projects Won" stroke={DASHBOARD_BLUE} strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
                        <Line type="monotone" dataKey="doiCreated" name="DOI Created" stroke={DASHBOARD_GREEN} strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
                        <Line type="monotone" dataKey="doiPending" name="DOI Pending" stroke={DASHBOARD_ORANGE} strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full rounded-lg border border-dashed border-gray-200 bg-gray-50 flex items-center justify-center text-sm text-gray-500">
                      No DOI trend data available
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white border border-slate-200/90 rounded-2xl p-5 shadow-2xs">
                <h2 className="text-base font-semibold text-gray-900">DOI Distribution</h2>
                <p className="text-xs text-gray-500 mt-1">Created versus pending DOI documentation.</p>
                <div className="h-72">
                  {dashboard.distributionData.some((item) => item.value > 0) ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={dashboard.distributionData} dataKey="value" nameKey="name" innerRadius={62} outerRadius={96} paddingAngle={3}>
                          {dashboard.distributionData.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full rounded-lg border border-dashed border-gray-200 bg-gray-50 flex items-center justify-center text-sm text-gray-500">
                      No DOI distribution data available
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200/90 rounded-2xl p-5 shadow-2xs">
              <h2 className="text-base font-semibold text-gray-900">Platform-wise DOI Compliance</h2>
              <p className="text-xs text-gray-500 mt-1">Compliance percentage across platforms.</p>
              <div className="h-72 mt-4">
                {dashboard.complianceData.some((item) => item.projectsWon > 0) ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dashboard.complianceData} margin={{ top: 10, right: 18, left: -10, bottom: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis
                        dataKey="platform"
                        tick={{ fontSize: 11 }}
                        interval={0}
                        angle={dashboard.complianceData.length > 4 ? -25 : 0}
                        textAnchor={dashboard.complianceData.length > 4 ? 'end' : 'middle'}
                        height={dashboard.complianceData.length > 4 ? 60 : 30}
                      />
                      <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
                      <Tooltip formatter={(value, name, item) => (
                        name === 'Compliance %'
                          ? [`${value}% (${item.payload.doiCreated}/${item.payload.projectsWon})`, name]
                          : [value, name]
                      )} />
                      <Bar dataKey="compliance" name="Compliance %" fill={DASHBOARD_BLUE} radius={[6, 6, 0, 0]} minPointSize={3} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full rounded-lg border border-dashed border-gray-200 bg-gray-50 flex items-center justify-center text-sm text-gray-500">
                    No platform compliance data available
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-white border border-slate-200/90 rounded-2xl p-5 pb-8 shadow-2xs">
                <h2 className="text-base font-semibold text-gray-900 mb-4">Platform Summary</h2>
                <div className="overflow-x-auto pb-3">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs font-semibold uppercase text-gray-500 border-b border-gray-200">
                        <th className="py-3 pr-4">Platform Name</th>
                        <th className="py-3 pr-4">Won Projects</th>
                        <th className="py-3 pr-4">DOI Created</th>
                        <th className="py-3 pr-4">DOI Pending</th>
                        <th className="py-3 pr-4">Compliance %</th>
                        <th className="py-3">Trend</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {dashboard.platformSummary.length === 0 ? (
                        <tr><td colSpan={6} className="py-8 text-center text-gray-500">No platform data available</td></tr>
                      ) : dashboard.platformSummary.map((row) => {
                        const direction = row.trend?.direction || 'neutral';
                        const TrendIcon = direction === 'down' ? TrendingDown : TrendingUp;
                        return (
                          <tr key={row.platform} className="text-gray-700">
                            <td className="py-3 pr-4 font-medium text-gray-900">{row.platform}</td>
                            <td className="py-3 pr-4">{row.projectsWon}</td>
                            <td className="py-3 pr-4">{row.doiCreated}</td>
                            <td className="py-3 pr-4">{row.doiPending}</td>
                            <td className="py-3 pr-4">{row.compliance}%</td>
                            <td className={`py-3 ${direction === 'down' ? 'text-red-600' : direction === 'up' ? 'text-green-600' : 'text-gray-500'}`}>
                              <div className="flex items-center gap-1">
                                <TrendIcon className="h-4 w-4" />
                                <span className="text-xs">{row.trend?.label || 'No Prior Data'}</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-white border border-slate-200/90 rounded-2xl p-5 pb-8 shadow-2xs">
                <h2 className="text-base font-semibold text-gray-900 mb-4">Recent DOI Activity</h2>
                <div className="overflow-x-auto pb-3">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs font-semibold uppercase text-gray-500 border-b border-gray-200">
                        <th className="py-3 pr-4">DOI Number</th>
                        <th className="py-3 pr-4">Project Name</th>
                        <th className="py-3 pr-4">Platform</th>
                        <th className="py-3 pr-4">Shared Date</th>
                        <th className="py-3 pr-4">Created By</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {dashboard.recentActivity.length === 0 ? (
                        <tr><td colSpan={7} className="py-8 text-center text-gray-500">No recent DOI activity</td></tr>
                      ) : dashboard.recentActivity.map((doi) => (
                        <tr key={doi._id || doi.doiNumber} className="text-gray-700">
                          <td className="py-3 pr-4 font-medium text-gray-900">{doi.doiNumber || 'N/A'}</td>
                          <td className="py-3 pr-4 max-w-[180px] truncate" title={doi.projectName || 'N/A'}>{doi.projectName || 'N/A'}</td>
                          <td className="py-3 pr-4">{doi.platform || 'Unassigned'}</td>
                          <td className="py-3 pr-4">{doi.sharedDate ? formatDate(doi.sharedDate, { includeTime: true }) : '-'}</td>
                          <td className="py-3 pr-4">{doi.createdBy || 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200/90 rounded-2xl p-5 shadow-2xs">
              <h2 className="text-base font-semibold text-gray-900">Insights</h2>
              <div className="mt-4 space-y-3">
                {dashboard.insights.map((insight, index) => (
                  <div key={insight} className="flex items-start gap-3">
                    <div className="h-7 w-7 rounded-full bg-blue-50 text-[#0F2942] flex items-center justify-center flex-shrink-0 text-xs font-semibold">{index + 1}</div>
                    <p className="text-sm text-gray-700 leading-5">{insight}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border border-slate-200/90 rounded-2xl p-5 shadow-2xs">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-gray-900">Alerts</h2>
                <span className="text-xs text-gray-500">{dashboard.alerts.length} open</span>
              </div>
              <div className="mt-4 space-y-3">
                {dashboard.alerts.length === 0 ? (
                  <div className="rounded-lg border border-green-100 bg-green-50 px-3 py-3 text-sm text-green-700">No DOI compliance alerts at this time.</div>
                ) : dashboard.alerts.map((alert) => (
                  <div key={alert.type || alert.message} className="flex items-start gap-3 rounded-lg border border-amber-100 bg-amber-50 px-3 py-3">
                    <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-amber-800 leading-5">{alert.message}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoiDashboard;
